import { Task, TaskType, IconNames, FEATURE_FLAGS } from "@/types/v2";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { Checkbox } from "@/components/Base/Checkbox";
import { useFeature } from "@/hooks/useFeatures";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import { isOverdue } from "@/lib/taskSelectors";
import { cn, firestoreToDateTime, getTextColor } from "@/lib/utils";
import { format } from "date-fns";
import { MoreVertical, PaperclipIcon } from "lucide-react";
import { TYPE_BADGE_CLASSES } from "@/lib/const.ts";

export interface TaskRowProps {
  task: Task;
  roNumber?: string;
  assigneeName?: string;
  assigneeInitials?: string;
  assigneeColor?: string;
  canModify: boolean;
  disabledReason?: string;
  onToggleComplete: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  /** Opens the RO side panel for this task's related card. Omit to render the
   *  RO number as plain text (the RO-scoped strip has no RO to jump to). */
  onRoClick?: (task: Task) => void;
}

const TYPE_LABELS: Record<TaskType, string> = {
  [TaskType.GENERAL]: "General",
  [TaskType.UPDATE]: "Update",
  [TaskType.PARTS]: "Parts",
  [TaskType.OTHER]: "Other",
};

export const TaskRow = ({
  task,
  roNumber,
  assigneeName,
  assigneeInitials,
  assigneeColor,
  canModify,
  disabledReason,
  onToggleComplete,
  onEdit,
  onDelete,
  onRoClick,
}: TaskRowProps) => {
  const due = firestoreToDateTime(task.dueDate);
  const overdue = isOverdue(task);
  const openEdit = canModify ? () => onEdit(task) : undefined;
  const isAttachmentsEnabled = useFeature(FEATURE_FLAGS.isAttachmentsEnabled);

  const roChipClasses =
    "shrink-0 rounded bg-card px-1.5 py-0.5 text-[11px] font-semibold leading-none text-muted-foreground";

  const checkbox = (
    <Checkbox
      checked={task.completed}
      disabled={!canModify}
      onCheckedChange={() => onToggleComplete(task)}
      aria-label={task.completed ? "Mark incomplete" : "Mark complete"}
      className={cn(
        "mt-0.5 size-5 rounded-md border-2 bg-popover",
        // Explicit border color: the shared Checkbox's border-primary-foreground
        // is near-white and invisible on this light card.
        "border-input hover:border-ring",
        "disabled:bg-background disabled:opacity-100",
        "data-[state=checked]:border-success data-[state=checked]:bg-success data-[state=checked]:text-success-foreground"
      )}
    />
  );

  return (
    <TooltipProvider>
      <div className="group flex items-start gap-3 rounded-lg border border-border bg-popover px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md">
        {canModify ? (
          checkbox
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{checkbox}</span>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        )}
        {/* A div, not a button: the RO chip below is itself a button, and a
            button cannot legally nest inside one. */}
        <div
          role={openEdit ? "button" : undefined}
          tabIndex={openEdit ? 0 : undefined}
          onClick={openEdit}
          onKeyDown={(e) => {
            if (!openEdit) return;
            // Only the row itself, not a nested control like the RO chip
            // below — keydown bubbles up from it and would otherwise hijack
            // Enter/Space and open the edit dialog instead of the RO panel.
            if (e.target !== e.currentTarget) return;
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            openEdit();
          }}
          className={cn(
            "flex min-w-0 flex-1 flex-col items-start gap-1.5 border-0 bg-transparent p-0 text-left",
            openEdit ? "cursor-pointer" : "cursor-default"
          )}
        >
          <div className="flex min-w-0 items-center gap-2 self-stretch">
            {roNumber &&
              (onRoClick ? (
                <button
                  type="button"
                  // Stop the click reaching the row, which would open the edit
                  // dialog on top of the panel we just opened.
                  onClick={(e) => {
                    e.stopPropagation();
                    onRoClick(task);
                  }}
                  title={`Open ${roNumber}`}
                  className={cn(
                    roChipClasses,
                    "underline-offset-2 hover:bg-background hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  )}
                >
                  {roNumber}
                </button>
              ) : (
                <span className={roChipClasses}>{roNumber}</span>
              ))}
            <span
              className={cn(
                "truncate text-sm font-semibold leading-[130%] text-foreground",
                task.completed && "text-muted-foreground line-through"
              )}
            >
              {task.title}
            </span>
          </div>
          {task.description && (
            <span className="line-clamp-1 text-xs leading-[140%] text-muted-foreground">
              {task.description}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-0.5">
            {assigneeInitials ? (
              <span
                className="flex size-6 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: assigneeColor }}
                title={assigneeName}
              >
                <span
                  className="text-[10px] font-semibold leading-none"
                  // getTextColor only parses 6-digit hex; the "#333"
                  // no-assignee-color fallback yields undefined → white.
                  style={{
                    color: getTextColor(assigneeColor ?? "") ?? "white",
                  }}
                >
                  {assigneeInitials}
                </span>
              </span>
            ) : (
              <span className="text-xs leading-none text-muted-foreground">
                Unassigned
              </span>
            )}
            {due && (
              <span
                className={cn(
                  "text-xs leading-none",
                  overdue
                    ? "font-medium text-destructive"
                    : "text-muted-foreground"
                )}
              >
                Due {format(due, "MMM d, h:mm a")}
              </span>
            )}
            <span
              className={cn(
                "flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none",
                TYPE_BADGE_CLASSES[task.type]
              )}
            >
              {TYPE_LABELS[task.type]}
            </span>
            {isAttachmentsEnabled && !!task.attachmentCount && (
              <span
                aria-label={`${task.attachmentCount} attachment${task.attachmentCount === 1 ? "" : "s"}`}
                className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold leading-none text-muted-foreground"
              >
                <PaperclipIcon className="h-3 w-3" />
                {task.attachmentCount}
              </span>
            )}
            {task.isPrivate && (
              <span className="flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold leading-none text-white">
                <Icon name={IconNames.lock} className="h-3 w-3" />
                Private
              </span>
            )}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="size-7 shrink-0 p-0 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label="Task actions"
            >
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={!canModify}
              onClick={() => onEdit(task)}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!canModify}
              onClick={() => onToggleComplete(task)}
            >
              {task.completed ? "Mark incomplete" : "Mark complete"}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!canModify}
              onClick={() => onDelete(task)}
              className="text-destructive focus:text-destructive"
            >
              Delete
            </DropdownMenuItem>
            {!canModify && disabledReason && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="max-w-56 whitespace-normal text-xs font-normal text-muted-foreground">
                  {disabledReason}
                </DropdownMenuLabel>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
};
